import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeaturePicker } from '@/components/feature-picker';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, ErrorState, Skeleton, useToast } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { normalizeFeatures, type Feature } from '@/lib/features';
import { useTrade } from '@/lib/trade';
import { BUSINESS_SETTINGS_KEY, businessApi } from './business-api';

/** מה מוצג לעסק. שמירה טוענת מחדש — התפריט והמסכים נגזרים מזה. */
export function FeaturesPanel() {
  const { t } = useTranslation(['settings', 'common']);
  const toast = useToast();
  const trade = useTrade();
  const settings = useQuery({ queryKey: BUSINESS_SETTINGS_KEY, queryFn: ({ signal }) => businessApi.get(signal) });
  const [draft, setDraft] = useState<Feature[] | null>(null);
  useEffect(() => {
    if (settings.data && !draft) setDraft(normalizeFeatures(settings.data.features ?? []));
  }, [settings.data, draft]);

  const save = useMutation({
    mutationFn: (features: Feature[]) => businessApi.update({ features }),
    onSuccess: () => {
      toast.success(t('features.saved'), t('features.savedHint'));
      window.location.reload();
    },
    onError: (e) => toast.error(t('common:errors.saveFailed'), e instanceof ApiError ? e.message : undefined),
  });

  if (settings.isError) return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;
  if (!draft) return <Skeleton className="h-96" />;
  const changed = JSON.stringify(draft) !== JSON.stringify(normalizeFeatures(settings.data?.features ?? []));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('features.title')}</CardTitle>
        <CardDescription>{t('features.description', { trade: trade.label })}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FeaturePicker value={draft} onChange={setDraft} />
        <div className="flex justify-end">
          <Button disabled={!changed} loading={save.isPending} onClick={() => save.mutate(draft)}>
            {t('common:actions.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
